/**
 * Ausfuhr-Korrektur bei Erstattung (Plan 2026-10-28, Task 5) — der Dialog am Knopf
 * „Issue export correction" in den Refund-Entscheidungen.
 *
 *   GET  /api/invoicing/admin/ausfuhr-korrektur/kontext?refund_id=   → der Kopf
 *   POST /api/invoicing/admin/ausfuhr-korrektur/vorschau             → Entwurfs-PDF
 *   POST /api/invoicing/admin/ausfuhr-korrektur/ausstellen           → das Dokument
 *
 * Ein Mitarbeiter hat die USt einer Selbstausfuhr in Shopify erstattet. Der Dialog nimmt
 * die Ausfuhrangaben auf, die keine Nachweiszeile trägt (Plan B5: Ausfuhrtag,
 * Zollstelle, deren Land, „Original liegt vor", „Privatperson, Reisegepäck"), und stellt
 * die 0-%-Rechnungskorrektur aus. Bauform wie `KorrekturDialog.jsx`, ohne dessen
 * Zeilentabelle — die Korrektur hat genau eine Zeile, und die baut der Server.
 *
 * 🔴 **Der Server prüft, der Client nennt.** Die Halte (Frist, Wertgrenze, EU-Zollstelle,
 *    Abnehmer, Original) stehen in `backend/invoicing/ausfuhr_regeln.py`. Der Client sperrt
 *    nur, was er sicher weiss: fehlende Pflichtfelder, einen Ausfuhrtag nach heute (Berlin)
 *    und die Gründe, die `kontext` schon nennt. Ein fehlender Haken ist ein Nein und geht
 *    so hinaus — den Halt sagt der Server, als Satz aus `FEHLER_TEXT`.
 *
 * 🔴 **Kein roher Code** (R16-W1). Jeder Code, den die drei Python-Dateien an den Client
 *    geben, hat hier einen Satz; die Probe T3 liest die Codes aus der Quelle und verlangt
 *    Gleichheit in beiden Richtungen.
 *
 * 🔴 **Unbekannt heisst unbekannt** (Muster BA3b, `InvoiceDetail.jsx`). Ein 5xx oder ein
 *    Abbruch nach dem Absenden kann NACH dem Commit kommen. Der Dialog sagt „may or may
 *    not", lässt die Liste neu laden und bietet nur noch „Close". Vor dem Kern schreibt
 *    der Server nichts; deshalb sind 4xx-Antworten definierte Ausgänge (Task 4 benutzt 422,
 *    nicht 502, für einen nicht lesbaren Refund — 5xx bleibt dem unbekannten Ausgang).
 *
 * 🔴 **Doppelsenden** (R15-Lehre BA3b C2): eine Ref sperrt synchron. Nach einem endgültigen
 *    Ausgang (`ausgestellt`, `schon_erledigt`, unbekannt) bleibt sie gesperrt, bis der Dialog
 *    neu öffnet.
 *
 * 🔴 **Die Rückfrage ist ein gestapelter `ConfirmDialog`** (Pattern-Library §„Modal from
 *    modal", Form „stack"; R16 B1, Wiedervorlage 29.10.). Der äussere Dialog hält Eingaben,
 *    die ein Schliessen verlöre, also bleibt er offen. MUI macht ihn unter der Rückfrage
 *    inert, Esc schliesst nur die Rückfrage, und der Fokus kehrt auf „Issue correction"
 *    zurück. Bis dahin war die Rückfrage ein eingeschobener Teilschritt — eine dritte Form,
 *    gemessen mit vier Folgen (Frage hinter der Vorschau, toter Klick, Fokus auf dem
 *    Behälter, Esc schloss alles). `InlineConfirmButton` passt hier nicht: sein Esc hört auch
 *    der äussere Dialog. Ergebnis und Fehler stehen weiter im äusseren Dialog.
 *
 * Die API-Pfade stehen als vollständige Literale, damit das Endpunkt-Register
 * (`scripts/endpunkt_register.py`) den Aufrufer findet.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { api } from '../../api/client'
import { ConfirmDialog } from '../../components/common/confirm'
import PdfInlineFrame from '../../components/common/PdfInlineFrame'
import DisabledReason from '../../components/common/DisabledReason'
import { FS, PANEL_RADIUS, SP } from '../../theme/tokens'
import { berlinDateISO, formatBerlinDate } from '../../utils/time'
// german-words: ok: Importpfad, kein Sichttext
import { HAND_LAENDER } from './Betrieb'

const STRICH = '—'

/** Die Beschriftung der fünf Eingabefelder (`ausfuhr_regeln.EINGABE_FELDER`). */
export const FELD_LABEL = {
  ausfuhrtag: 'Export date',
  zollstelle: 'Customs office',
  zollstelle_land: 'Country of the customs office',
  original_liegt_vor: 'Original stamped invoice is with us',
  privat_reisegepaeck: 'Private customer, goods carried in personal luggage',
}

//: Der Wert der Auswahl „Other". Kein Ländercode — der Server hält mit `zollstelle_nicht_eu`.
export const LAND_ANDERES = 'OTHER'

/** Die EU-Länder der Auswahl — dieselbe Liste wie die Handrechnung (`Betrieb.HAND_LAENDER`). */
const EU_LAENDER = HAND_LAENDER.filter((l) => l.eu)

/**
 * Je Server-Code ein Satz (Bedienertext, englisch). Ein Eintrag ist ein Satz oder eine
 * Funktion des Werts hinter dem Doppelpunkt (`modus:pilot`, `eingabe_fehlt:zollstelle`).
 *
 * Herkunft (T3 liest sie aus der Quelle):
 *   Task 1 `ausfuhr_regeln.py`      die zehn Halte, `eingabe_fehlt`, `ausfuhrtag_unlesbar`
 *   Task 2 `ausfuhr_korrektur.py`   `sperrgrund` (`modus`, `rechts_gate_offen`, `korrekturmodus`)
 *   Task 3 `ausfuhr_korrektur.py`   Erstattungslage, Vorflug-Gründe, Kern, beide Text-Riegel
 *   Task 4 `ausfuhr_korrektur_routes.py`  die Fehlercodes der Routen
 *
 * ⚠️ `original_fehlt` hat zwei Quellen mit verschiedener Bedeutung: der Halt (Haken
 * „Original liegt vor" fehlt, `ausfuhr_regeln.halte`) und der Kern (die Originalrechnung
 * ist nicht mehr da, praktisch unerreichbar — die Route antwortet vorher 409
 * `original_unbekannt`). Ein Code, ein Satz: er nennt beide.
 */
export const FEHLER_TEXT = {
  // ── Task 1: die Halte (`ausfuhr_regeln.HALTECODES`) und die Eingaben ─────────────
  wertgrenze:
    'The invoice total including VAT is not above €50 — a tax-free export in personal luggage does not apply. Handle this refund manually.',
  liefertag_fehlt:
    'The invoice has no delivery date, so the export deadline cannot be checked. Handle this refund manually.',
  ausfuhrtag_vor_lieferung:
    'The export date lies before the delivery date. Check the date stamped by customs.',
  ausfuhrtag_in_zukunft:
    'The export date lies in the future. Enter the date stamped by customs.',
  frist_ueberschritten:
    'The goods left the EU after the deadline (end of the third month after delivery). Handle this refund manually.',
  ausfuhrtag_nach_nachweis_eingang:
    'The export date lies after the day the proof arrived. Check the date stamped by customs.',
  zollstelle_nicht_eu:
    'The customs office must be the exit office of an EU country. If the stamp is not from one, handle this refund manually.',
  abnehmer_nicht_drittland:
    'The invoice address is not in a country outside the EU. Handle this refund manually.',
  abnehmer_unternehmer_handweg:
    'Only a private customer carrying the goods in personal luggage qualifies. For any other customer, handle this refund manually.',
  original_fehlt:
    'The original stamped invoice is not confirmed as being with us, or the original invoice no longer exists. Without it, handle this refund manually.',
  eingabe_fehlt: (feld) => `${FELD_LABEL[feld] || 'A required field'} is missing. Fill it in and try again.`,
  ausfuhrtag_unlesbar: 'The export date could not be read. Pick the date again.',

  // ── Task 2: der Sperrgrund (`ausfuhr_korrektur.sperrgrund`) ─────────────────────
  modus: (wert) => (wert === 'pilot'
    ? 'Export corrections run as a pilot, and this order is not on the pilot list — nothing is issued.'
    : wert === 'off' || !wert
      ? 'Export corrections are switched off — nothing is issued.'
      : `Export corrections are set to “${wert}” — nothing is issued.`),
  rechts_gate_offen:
    'The legal basis for export corrections has not been approved yet — nothing is issued.',
  korrekturmodus:
    'The general correction mode does not allow a correction for this order — nothing is issued.',

  // ── Task 3: die Text-Riegel ──────────────────────────────────────────────────────
  ausfuhrkorrekturtext_nicht_freigegeben:
    'The wording of the export correction has not been released yet — issuing is blocked, the preview works.',
  korrekturtext_nicht_freigegeben:
    'The correction texts have not been released yet — nothing was issued.',

  // ── Task 3: die Erstattungslage (`ausfuhr_korrektur.LAGE_CODES`) ─────────────────
  refund_unbekannt:
    'This refund no longer appears on the order in Shopify. Reload the list.',
  refund_unlesbar:
    "Shopify's refund data could not be read — try again later; if it persists, handle this refund manually.",
  refund_datum_fehlt:
    'The refund has no date in Shopify, so the tax period is unknown. Try again later.',
  nicht_endgueltig:
    'The refund is not final in Shopify yet. Wait until it has completed, then try again.',
  zahlungsstoerung:
    'The order has a dispute or a failed payment. Handle this refund manually.',
  erstattung_mit_positionen:
    'The refund also returns items or shipping — an export correction covers a VAT-only refund. Handle this refund manually.',
  weitere_erstattung:
    'Another refund on this order has moved money or is still pending — handle this refund manually.',

  // ── Task 3: die Vorflug-Gründe (`ausfuhr_korrektur.grundgruende`) und der Kern ──
  original_status: (wert) =>
    `The original invoice is in state “${wert}” — no correction is made from it.`,
  original_steuerfall: (wert) =>
    `The original invoice is not a domestic invoice (tax case “${wert}”) — an export correction is not possible.`,
  nicht_eur: 'The original invoice is not in euros. Handle this refund manually.',
  betrag_null: 'The original invoice carries no VAT — there is nothing to correct.',
  betrag_nicht_volle_ust:
    'The refunded amount is not exactly the VAT of the invoice — only a full VAT refund can be corrected here. Handle this refund manually.',
  schon_korrigiert:
    'This invoice has already been corrected. Handle this refund manually.',
  nachweis_nicht_freigegeben:
    'There is no approved export proof for this invoice. Approve the proof on the invoice first.',
  erstattung_vor_nachweis:
    'The refund was made before the export proof arrived. Handle this refund manually.',
  vorflug: (wert) =>
    `The correction could not be rendered (${String(wert ?? '').split(':')[0] || 'unknown'}) — nothing was issued.`,
  ausstellung: (wert) =>
    `Issuing failed unexpectedly (${String(wert ?? '').split(':')[0] || 'unknown'}) — nothing was issued.`,
  entscheidung_nicht_offen:
    'This refund decision is no longer open — nothing was issued. Reload the list.',
  nachweis_gewechselt:
    'The export proof changed while issuing — nothing was issued. Close the dialog and open it again.',
  entscheidung_gewechselt:
    'The refund decision changed while issuing — nothing was issued. Reload the list.',
  status: (wert) => `A correction for this refund is already in state “${wert}”.`,

  // ── Task 4: die Routen (`ausfuhr_korrektur_routes.py`) ───────────────────────────
  refund_id_fehlt: 'The request names no refund.',
  refund_id_ungueltig: 'The refund identifier is invalid.',
  entscheidung_unbekannt:
    'There is no refund decision for this refund yet. It appears within 24 hours at the latest — reload the list later.',
  zustand_nicht_offen: 'This decision is no longer open. Reload the list.',
  art_nicht_zulaessig: 'This decision does not lead to an export correction.',
  original_unbekannt:
    'No Forge invoice is stored for this decision — an export correction is not possible here.',
  kein_ausfuhrvorgang:
    'This invoice has no self-export case — an export correction is not possible.',
  original_weicht_ab:
    'The original invoice has changed. Close the dialog and open it again.',
  eingaben_ungueltig: 'Some entries are missing or invalid. The details are below.',
  ausfuhr_korrektur_gesperrt: 'The export correction was not issued. The reasons are below.',
  ausfuhr_korrektur_aus: 'Export corrections are not enabled for this order. The reason is below.',
  kein_schreibkonto:
    'This account may not issue a correction. Sign in with the write account and try again.',
}

/** Ein Code (mit oder ohne `:wert`) als Satz. Unbekanntes bleibt sichtbar, nie still. */
export function satz(roh) {
  const text = String(roh ?? '')
  const code = text.split(':')[0]
  const wert = text.slice(code.length + 1) || undefined
  const eintrag = FEHLER_TEXT[code]
  if (typeof eintrag === 'function') return eintrag(wert)
  return eintrag || `Unknown reason (${text}).`
}

/** Die Ausgänge des Ausstellens, je eigener Satz (Grill W14). */
export const AUSGANG_TEXT = {
  ausgestellt: 'The export correction has been issued.',
  schon_erledigt: 'Already issued:',
  ohne_dokument:
    'This refund was already closed without a document — no correction was issued.',
  unbekannt:
    'The export correction may or may not have been issued — the list is being reloaded.',
}

/** 401 kommt vom Tor, VOR jedem Schreiben — ein definierter Ausgang. */
export const SITZUNG_SATZ = 'Your session has expired — please sign in again. Nothing was issued.'

/**
 * Die `kontext`-Codes, bei denen ein neuer Versuch SICHER dieselbe Antwort gibt (R16 W4,
 * Muster `KorrekturDialog.DAUERHAFTE_LADECODES`): dort kein „Try again", und die gesperrten
 * Knöpfe nennen „Not available for this refund." statt eines Ladefehlers. Vorübergehend
 * bleiben `entscheidung_unbekannt` (die Zeile kommt binnen 24 h) und die Erstattungscodes.
 */
export const DAUERHAFTE_LADECODES = new Set([
  'kein_ausfuhrvorgang', 'art_nicht_zulaessig', 'original_unbekannt', 'zustand_nicht_offen',
])

const VORSCHAU_RUECKFALL = 'The preview could not be created. Try again.'

/** Eine Antwort, die als Blob kam (axios `responseType: 'blob'`), auspacken. */
async function alsJson(daten) {
  if (daten && typeof daten.text === 'function' && typeof daten.type === 'string') {
    try { return JSON.parse(await daten.text()) } catch { return null }
  }
  return daten
}

/**
 * Deutet eine gescheiterte Anfrage: `{ text, gruende, unbekannt }`.
 * Definiert ist ein 4xx mit einem bekannten Code (und 401); alles andere ist unbekannt.
 */
async function deute(e) {
  const status = e?.response?.status
  if (status === 401) return { text: SITZUNG_SATZ, gruende: [], unbekannt: false }
  const daten = await alsJson(e?.response?.data)
  const code = daten?.error
  if (status && status < 500 && code && Object.prototype.hasOwnProperty.call(FEHLER_TEXT, code)) {
    // `ausfuhr_korrektur_aus` trägt den Sperrgrund doppelt (`sperrgrund` und `gruende`);
    // eine Antwort ohne `gruende` fällt auf `sperrgrund` zurück.
    const gruende = Array.isArray(daten.gruende) && daten.gruende.length
      ? daten.gruende
      : (daten.sperrgrund ? [daten.sperrgrund] : [])
    return { text: satz(code), gruende, unbekannt: false }
  }
  return { text: null, gruende: [], unbekannt: true }
}

const LEER = { ausfuhrtag: '', zollstelle: '', zollstelle_land: '', original_liegt_vor: false,
  privat_reisegepaeck: false }

const geld = (wert) => (wert == null || wert === '' ? STRICH : `${wert} €`)
const tag = (iso) => (iso ? formatBerlinDate(iso, { dateOnly: true }) : STRICH)

/**
 * @param offen         Dialog offen?
 * @param refundId      der Refund der Entscheidung
 * @param onSchliessen  Dialog schliessen
 * @param onErgebnis    (text, ausgang) — die Seite lädt neu und hält den Satz; `ausgang`
 *                      ist die Antwort (`zustand`, `nummer`, `ohne_dokument`) oder
 *                      `{ zustand: 'unbekannt' }`
 */
export default function AusfuhrKorrekturDialog({ offen, refundId, onSchliessen, onErgebnis }) {
  const theme = useTheme()
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  const titelId = useId()

  const [kontext, setKontext] = useState(null)
  const [laedt, setLaedt] = useState(false)
  const [ladefehler, setLadefehler] = useState(null)
  const [ladeCode, setLadeCode] = useState(null)
  const [form, setForm] = useState(LEER)
  const [sendet, setSendet] = useState(false)
  const [bestaetigen, setBestaetigen] = useState(false)
  const [fehler, setFehler] = useState(null)        // { text, gruende }
  const [vorschau, setVorschau] = useState(null)    // { url }
  const [ergebnis, setErgebnis] = useState(null)    // 200-Antwort
  const [beendet, setBeendet] = useState(false)     // nur noch „Close"
  // R15-Lehre BA3b C2: die Ref sperrt SYNCHRON; nach einem endgültigen Ausgang bleibt
  // sie stehen, bis der Dialog neu öffnet.
  const sendetRef = useRef(false)
  const ausstellenRef = useRef(null)
  const schliessenRef = useRef(null)
  const warBestaetigen = useRef(false)

  // Die Objekt-URL wird ausdrücklich freigegeben (Muster KorrekturDialog — unter
  // StrictMode zöge ein Aufräum-Effekt sie unter dem Rahmen weg).
  const vorschauUrl = useRef(null)
  const setzeVorschau = useCallback((neu) => {
    if (vorschauUrl.current && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(vorschauUrl.current)
    }
    vorschauUrl.current = neu?.url || null
    setVorschau(neu)
  }, [])

  const laden = useCallback(() => {
    if (!refundId) return undefined
    let abgemeldet = false
    setLaedt(true)
    setLadefehler(null)
    setLadeCode(null)
    api.get('/api/invoicing/admin/ausfuhr-korrektur/kontext', { params: { refund_id: refundId } })
      .then(({ data }) => {
        if (abgemeldet) return
        setKontext(data)
        setLaedt(false)
      })
      .catch(async (e) => {
        if (abgemeldet) return
        setLaedt(false)
        // 401 kommt vom Tor, vor jeder Route — der Rumpf trägt keinen Code dieser Fläche.
        if (e?.response?.status === 401) {
          setLadefehler(SITZUNG_SATZ)
          return
        }
        const daten = await alsJson(e?.response?.data)
        setLadeCode(daten?.error || null)
        setLadefehler(daten?.error ? satz(daten.error) : 'The data could not be loaded. Try again.')
      })
    return () => { abgemeldet = true }
  }, [refundId])

  useEffect(() => {
    if (!offen) {
      setKontext(null); setForm(LEER); setFehler(null); setzeVorschau(null)
      setErgebnis(null); setBeendet(false); setBestaetigen(false); setSendet(false)
      sendetRef.current = false
      return undefined
    }
    sendetRef.current = false
    return laden()
  }, [offen, laden, setzeVorschau])

  // WCAG 2.4.3: ist nur noch „Close" da, bekommt es den Fokus (Muster BA3b T4c).
  useEffect(() => {
    if (beendet) schliessenRef.current?.focus()
  }, [beendet])
  // Nach dem Schliessen der Rückfrage (Back, Esc, definierter Fehler) gehört der Fokus auf
  // „Issue correction". MUI stellt ihn selbst zurück, aber nur auf das Element, das beim
  // Öffnen fokussiert war — Safari fokussiert einen geklickten Knopf nicht. Dieser Effekt
  // läuft nach dem `TrapFocus` der Rückfrage (Eltern-Effekte nach Kind-Effekten).
  useEffect(() => {
    if (warBestaetigen.current && !bestaetigen && !beendet) ausstellenRef.current?.focus()
    warBestaetigen.current = bestaetigen
  }, [bestaetigen, beendet])

  const heute = berlinDateISO(new Date())
  const inZukunft = Boolean(form.ausfuhrtag) && form.ausfuhrtag > heute
  const pflichtFehlt = !form.ausfuhrtag || !form.zollstelle.trim() || !form.zollstelle_land
  const kontextGruende = kontext?.gruende || []
  const formGesperrt = !kontext || pflichtFehlt || inZukunft || kontextGruende.length > 0
  const ausstellenGesperrt = formGesperrt || Boolean(kontext?.sperrgrund)
    || !kontext?.muster_freigegeben

  const ladeDauerhaft = DAUERHAFTE_LADECODES.has(ladeCode)
  const grundFuerForm = !kontext
    ? (ladefehler ? (ladeDauerhaft ? 'Not available for this refund.' : 'The data could not be loaded.')
      : 'Loading …')
    : kontextGruende.length > 0 ? 'This refund cannot be corrected here — see the reasons above.'
      : pflichtFehlt ? 'Fill in the export date, the customs office and its country first.'
        : inZukunft ? 'The export date cannot be in the future.' : null
  const grundFuerAusstellen = grundFuerForm
    || (kontext?.sperrgrund ? 'Issuing is blocked — see the note above.'
      : !kontext?.muster_freigegeben ? 'The export correction wording is not released yet.' : null)

  // R16 B1 b: wird das Ausstellen gesperrt, während die Rückfrage offen ist (im Browser
  // ist der äussere Dialog darunter inert; ein Kontext-Wechsel bleibt möglich), schliesst
  // sie — ein „Confirm issue", das still nichts tut, darf es nicht geben.
  useEffect(() => {
    if (bestaetigen && ausstellenGesperrt && !sendet) setBestaetigen(false)
  }, [bestaetigen, ausstellenGesperrt, sendet])

  const setze = (feld) => (e) => {
    const wert = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((alt) => ({ ...alt, [feld]: wert }))
    setFehler(null)
  }

  const koerper = () => ({
    refund_id: refundId,
    original_id: kontext?.original?.id,
    ausfuhrtag: form.ausfuhrtag,
    zollstelle: form.zollstelle.trim(),
    zollstelle_land: form.zollstelle_land,
    original_liegt_vor: form.original_liegt_vor,
    privat_reisegepaeck: form.privat_reisegepaeck,
  })

  const holeVorschau = () => {
    if (formGesperrt || sendetRef.current || beendet) return
    sendetRef.current = true
    setSendet(true); setFehler(null); setzeVorschau(null)
    api.post('/api/invoicing/admin/ausfuhr-korrektur/vorschau', koerper(), { responseType: 'blob' })
      .then((antwort) => {
        const blob = antwort.data instanceof Blob
          ? antwort.data : new Blob([antwort.data], { type: 'application/pdf' })
        setzeVorschau({ url: URL.createObjectURL(blob) })
        sendetRef.current = false
        setSendet(false)
      })
      .catch(async (e) => {
        // Die Vorschau schreibt nichts — ein unbekannter Ausgang ist hier nur ein Fehlschlag.
        const deutung = await deute(e)
        sendetRef.current = false
        setSendet(false)
        setFehler({ text: deutung.text || VORSCHAU_RUECKFALL, gruende: deutung.gruende })
      })
  }

  const stelleAus = () => {
    if (ausstellenGesperrt || sendetRef.current || beendet) return
    sendetRef.current = true
    setSendet(true); setFehler(null)
    api.post('/api/invoicing/admin/ausfuhr-korrektur/ausstellen', koerper())
      .then(({ data }) => {
        // Endgültig: die Sperre bleibt bis zum nächsten Öffnen stehen.
        setSendet(false)
        setBestaetigen(false)
        setErgebnis(data)
        setBeendet(true)
        const text = data.zustand === 'ausgestellt'
          ? `${AUSGANG_TEXT.ausgestellt} Number ${data.nummer}.`
          : data.ohne_dokument || !data.nummer
            ? AUSGANG_TEXT.ohne_dokument
            : `${AUSGANG_TEXT.schon_erledigt} ${data.nummer}`
        onErgebnis?.(text, data)
      })
      .catch(async (e) => {
        const deutung = await deute(e)
        setSendet(false)
        setBestaetigen(false)
        if (deutung.unbekannt) {
          setFehler({ text: AUSGANG_TEXT.unbekannt, gruende: [] })
          setBeendet(true)
          onErgebnis?.(AUSGANG_TEXT.unbekannt, { zustand: 'unbekannt' })
          return
        }
        sendetRef.current = false
        setFehler(deutung)
      })
  }

  const ohneDokument = ergebnis?.zustand === 'schon_erledigt'
    && (ergebnis.ohne_dokument || !ergebnis.nummer)
  const ergebnisTon = ergebnis?.zustand === 'ausgestellt' ? 'success'
    : ohneDokument ? 'warning' : 'info'
  const ergebnisText = !ergebnis ? null
    : ergebnis.zustand === 'ausgestellt' ? `${AUSGANG_TEXT.ausgestellt} Number ${ergebnis.nummer}.`
      : ohneDokument ? AUSGANG_TEXT.ohne_dokument
        : `${AUSGANG_TEXT.schon_erledigt} ${ergebnis.nummer}`

  const original = kontext?.original || {}
  const nachweis = kontext?.nachweis

  return (
    <>
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id={titelId}>Issue export correction</DialogTitle>
      <DialogContent dividers>
        {laedt && (
          <Stack direction="row" spacing={SP.chip} alignItems="center" aria-busy="true">
            <CircularProgress size={20} aria-hidden="true" />
            <Typography variant="body2">Loading …</Typography>
          </Stack>
        )}

        {ladefehler && (
          <Alert
            severity="error"
            action={ladeDauerhaft
              ? null
              : <Button size="small" color="inherit" onClick={laden}>Try again</Button>}
          >
            {ladefehler}
          </Alert>
        )}

        {kontext && (
          <Stack spacing={SP.card}>
            <Box data-testid="ausfuhr-kopf">
              <Typography variant="body2">
                Invoice <strong>{original.nummer || original.id}</strong>
                {original.bestellung ? ` · ${original.bestellung}` : ''} · VAT {geld(original.ust)}
              </Typography>
              <Typography variant="body2">
                Refunded {geld(kontext.erstattet)} on {tag(kontext.refund_datum)}
              </Typography>
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                {nachweis?.geprueft_von
                  ? `Export proof approved by ${nachweis.geprueft_von} on ${tag(nachweis.geprueft_am)}`
                    + ` · proof received ${tag(nachweis.eingang)}`
                  : 'No approved export proof.'}
              </Typography>
            </Box>

            {/* Die Folge steht VOR dem Klick (R16-W1, Muster KorrekturDialog). */}
            <Typography variant="body2" data-testid="unwiderruflich" sx={{ color: 'text.secondary' }}>
              “Issue correction” corrects the invoice to 0 % VAT, consumes a correction number and
              cannot be undone. It is logged under your account, and the customer is sent the
              usual correction email.
            </Typography>

            {kontextGruende.length > 0 && (
              <Alert severity="warning" data-testid="kontext-gruende">
                This refund cannot be corrected here:
                <Box component="ul" sx={{ m: 0, mt: SP.chip, pl: SP.card }}>
                  {kontextGruende.map((g) => (
                    <Typography component="li" key={g} sx={{ fontSize: FS.sm }}>{satz(g)}</Typography>
                  ))}
                </Box>
              </Alert>
            )}
            {kontext.sperrgrund && (
              <Alert severity="info" data-testid="sperrgrund">
                {`${satz(kontext.sperrgrund)} The preview works.`}
              </Alert>
            )}
            {!kontext.muster_freigegeben && (
              <Alert severity="info">{FEHLER_TEXT.ausfuhrkorrekturtext_nicht_freigegeben}</Alert>
            )}

            <Stack direction={schmal ? 'column' : 'row'} spacing={SP.card}>
              <TextField
                size="small"
                type="date"
                label={FELD_LABEL.ausfuhrtag}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: heute }}
                value={form.ausfuhrtag}
                onChange={setze('ausfuhrtag')}
                error={inZukunft}
                helperText={inZukunft ? FEHLER_TEXT.ausfuhrtag_in_zukunft : 'Date stamped by customs'}
                disabled={sendet || beendet}
              />
              <TextField
                size="small"
                label={FELD_LABEL.zollstelle}
                value={form.zollstelle}
                onChange={setze('zollstelle')}
                helperText="Name of the EU exit office on the stamp"
                disabled={sendet || beendet}
                sx={{ flex: 1 }}
              />
            </Stack>
            {/* R16 B2 (Handrechnung): ein AUSWAHLFELD, kein Freitext. „Other" geht so
                hinaus; den Halt `zollstelle_nicht_eu` sagt der Server. */}
            <TextField
              size="small"
              select
              label={FELD_LABEL.zollstelle_land}
              value={form.zollstelle_land}
              onChange={setze('zollstelle_land')}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              disabled={sendet || beendet}
            >
              <option value="" disabled>Select a country</option>
              <optgroup label="European Union">
                {EU_LAENDER.map((l) => (
                  <option key={l.code} value={l.code}>{`${l.name} (${l.code})`}</option>
                ))}
              </optgroup>
              <option value={LAND_ANDERES}>Other</option>
            </TextField>
            <Box>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={form.original_liegt_vor}
                    onChange={setze('original_liegt_vor')}
                    disabled={sendet || beendet}
                  />
                )}
                label={FELD_LABEL.original_liegt_vor}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={form.privat_reisegepaeck}
                    onChange={setze('privat_reisegepaeck')}
                    disabled={sendet || beendet}
                  />
                )}
                label={FELD_LABEL.privat_reisegepaeck}
              />
            </Box>

            {fehler && (
              <Alert severity={beendet ? 'warning' : 'error'}>
                {fehler.text}
                {fehler.gruende?.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: SP.chip, pl: SP.card }}>
                    {fehler.gruende.map((g) => (
                      <Typography component="li" key={g} sx={{ fontSize: FS.sm }}>{satz(g)}</Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {vorschau && (
              <Box
                sx={{ border: 1, borderColor: 'divider', borderRadius: PANEL_RADIUS, p: SP.card }}
                aria-label="Preview — no document"
              >
                <Typography variant="subtitle2" sx={{ mb: SP.chip }}>
                  Preview — no document (draft DRAFT-000000)
                </Typography>
                <PdfInlineFrame url={vorschau.url} title="Export correction draft"
                                frameSx={{ width: '100%', height: 420, minHeight: 240 }} />
              </Box>
            )}

            {ergebnis && (
              <Alert severity={ergebnisTon} data-testid="ausfuhr-ergebnis">
                {ergebnisText}
                {/* Der Link hängt an der NUMMER (R16 O-4, Muster KorrekturDialog):
                    ohne Dokument zeigt er auf nichts. */}
                {ergebnis.nummer && ergebnis.invoice_id && (
                  <>
                    {' '}
                    <Box
                      component={RouterLink}
                      to={`/invoicing/${ergebnis.invoice_id}`}
                      onClick={onSchliessen}
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      To the document
                    </Box>
                  </>
                )}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {beendet && (
          <Button ref={schliessenRef} onClick={onSchliessen}>Close</Button>
        )}
        {!beendet && (
          <>
            <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
            <DisabledReason reason={sendet ? null : grundFuerForm}>
              <Button
                onClick={holeVorschau}
                loading={sendet}
                loadingPosition="start"
                aria-busy={sendet || undefined}
                disabled={formGesperrt || sendet}
              >
                Preview
              </Button>
            </DisabledReason>
            <DisabledReason reason={sendet ? null : grundFuerAusstellen}>
              <Button
                ref={ausstellenRef}
                variant="contained"
                onClick={() => { setFehler(null); setBestaetigen(true) }}
                disabled={ausstellenGesperrt || sendet}
              >
                Issue correction
              </Button>
            </DisabledReason>
          </>
        )}
      </DialogActions>
    </Dialog>

    {/* R16 B1: die Rückfrage, gestapelt über dem offenen Dialog (Hausform „stack").
        `busy` trägt den Lauf (K-F1) und sperrt Esc und „Back". */}
    <ConfirmDialog
      open={bestaetigen && !beendet}
      title="Confirm export correction"
      message={(
        <>
          Issue the export correction for invoice <strong>{original.nummer || original.id}</strong> now?
          It consumes a correction number and cannot be undone.
        </>
      )}
      confirmLabel="Confirm issue"
      cancelLabel="Back"
      busy={sendet}
      onConfirm={stelleAus}
      onCancel={() => setBestaetigen(false)}
    />
    </>
  )
}
